import React from "react";
import { useAuth } from "../../hooks/useAuth";
import { PUBLIC_CAREERS_URL } from "../../utils/constants";

/**
 * ProtectedRoute component that renders children only if user is authenticated
 * Shows loading state while authentication is being checked
 * Shows login prompt for unauthenticated users
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Components to render when authenticated
 * @param {React.ReactNode} props.fallback - Optional custom fallback component for unauthenticated users
 */
const ProtectedRoute = ({ children, fallback }) => {
  const { isLoading, isAuthenticated, loginWithRedirect, error } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <div className="loading-container">
          <div className="loading-spinner" data-testid="loading-spinner"></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show error state if there's an authentication error
  if (error) {
    return (
      <div className="protected-route-error">
        <div className="error-container">
          <h2>Authentication Error</h2>
          <p>There was an error with authentication: {error}</p>
          <button
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If authenticated, render the protected content
  if (isAuthenticated) {
    return children;
  }

  // If not authenticated, show login prompt or custom fallback
  if (fallback) {
    return fallback;
  }

  // Default unauthenticated state
  return (
    <div className="protected-route-unauthorized">
      <div className="unauthorized-container">
        <h2>Access Restricted</h2>
        <p>You need to be logged in to access this page.</p>
        <p>Please log in with your employee credentials to continue.</p>
        <button
          className="login-required-btn"
          onClick={() =>
            loginWithRedirect({
              appState: {
                returnTo: window.location.pathname,
              },
            })
          }
        >
          Employee Login
        </button>
        <button
          className="back-to-home-btn"
          onClick={() =>
            (window.location.href = PUBLIC_CAREERS_URL)
          }
        >
          Back to Careers Page
        </button>
      </div>
    </div>
  );
};

export default ProtectedRoute;
